import React, { useEffect, useState } from "react"
import { ThemeContext } from "./useTheme"

export type Theme = "light" | "dark"
// Light is the default for everyone — we deliberately ignore `prefers-color-scheme`
// so an OS-level dark setting does not opt users into dark mode they never asked for.
// Dark is opt-in: toggling (command bar, ⌘⇧., Account settings) persists the choice.
const DEFAULT_THEME: Theme = "light"

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>("light") // Start with light theme
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize theme on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("theme") as Theme
      setTheme(savedTheme === "light" || savedTheme === "dark" ? savedTheme : DEFAULT_THEME)
      setIsInitialized(true)
    }
  }, [])

  useEffect(() => {
    if (!isInitialized) return

    // Apply theme to html element
    const root = document.documentElement
    if (theme === "dark") {
      root.classList.add("dark")
      root.classList.remove("light")
    } else {
      root.classList.add("light")
      root.classList.remove("dark")
    }

    // Save to local storage
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", theme)
    }
  }, [theme, isInitialized])

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"))
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>{children}</ThemeContext.Provider>
}
